import React from 'react';
import {
  IconButton,
  ToggleGroup,
  View,
  Flex,
  Img,
  Text,
  Pill,
  Table,
  Button,
  IconCheckMarkSolid,
} from '@instructure/ui';
import { getStore, useCheckinStore } from '../../../stores/StoreContext';
import ItemIcon from './ItemIcon';
/**
 * A CheckinGroup is a component that displays a group of checkin items.
 *
 * The component displays a summary of the group, including the title, the number
 * of items in the group, and the number of items that have been reviewed and
 * accepted, rejected, or opted out.
 *
 * The component also displays a table of the checkin items in the group, with
 * columns for the item's title, comment, and status. The status column displays
 * an icon indicating the status of the item, and a button that allows the user
 * to edit the status of the item.
 *
 * The component also displays a button in the summary section that allows the
 * user to mark all items in the group as complete.
 *
 * The component receives the group as a prop, which should be an object with the
 * following properties:
 * - id: a unique identifier for the group
 * - title: the title of the group
 * - items: an array of objects, each representing a checkin item in the group
 *   - id: a unique identifier for the item
 *   - title: the title of the item
 *   - comment: the comment for the item
 *   - status: the status of the item (should be one of "accept", "reject", "optout", or "")
 * - complete: a boolean indicating whether all items in the group have been reviewed and accepted
 * - accepted: the number of items in the group that have been accepted
 * - rejected: the number of items in the group that have been rejected
 * - optedOut: the number of items in the group that have been opted out
 * - unreviewed: the number of items in the group that have not been reviewed
 *
 * @param {Object} props - an object with the following properties:
 *   - group: an object representing the checkin group
 */
const CheckinGroup = ({ group }) => {
  const CheckinStore = getStore('checkinStore');

  const completeCount = useCheckinStore(
    (state) => state.groups[group.id]?.complete || 0
  );
  const totalCount =
    useCheckinStore((state) => state.groups[group.id]?.total) || 0;
  const acceptCount = useCheckinStore(
    (state) => state.groups[group.id]?.accepted || 0
  );
  const rejectCount = useCheckinStore(
    (state) => state.groups[group.id]?.rejected || 0
  );
  const optoutCount = useCheckinStore(
    (state) => state.groups[group.id]?.optedOut || 0
  );
  const unreviewedCount = useCheckinStore(
    (state) => state.groups[group.id]?.unreviewed || 0
  );

  /**
   * Render a button for the given checkin item. If the item has not been
   * reviewed, the button will be a "Review" button with a primary color. If
   * the item has been rejected, the button will be a "Resolve" button with a
   * danger color. If the item has been accepted or opted out, the button will
   * be an "Edit" button with a primary color. The button will be a small size
   * and will trigger the setActiveItem action in the checkin store when clicked.
   *
   * @param {Object} item - an object representing a checkin item
   * @returns {React.Component} - a Button component
   */
  const renderButton = (item) => {
    let color = 'primary';
    let text = 'Review';
    const statusOptions = CheckinStore.statusOptions;

    if (item.status === statusOptions.REJECT.value) {
      text = 'Resolve';
      color = 'danger';
    } else if (item.status) {
      text = 'Edit';
    }

    return (
      <Button
        size="small"
        color={color}
        onClick={() => {
          CheckinStore.setActiveItem(item);
        }}
      >
        {text}
      </Button>
    );
  };

  return (
    <ToggleGroup
      toggleLabel="This is the toggle button label for screenreaders"
      margin="xx-small none none none"
      summary={
        <Flex
          justifyItems="space-between"
          margin="none small none none"
          id="GroupHeading"
        >
          <Flex.Item>
            <>
              <View margin="none x-small none none">
                <Text>{group.title}</Text>
              </View>
              {unreviewedCount > 0 && (
                <Pill
                  renderIcon={CheckinStore.statusOptions.UNKNOWN.icon}
                  margin="none none none x-small"
                >
                  <Text size="x-small" fontStyle="italic">
                    {unreviewedCount}
                  </Text>
                </Pill>
              )}
              {optoutCount > 0 && (
                <Pill
                  color="success"
                  renderIcon={CheckinStore.statusOptions.OPTOUT.icon}
                  margin="none none none x-small"
                >
                  <Text size="x-small" fontStyle="italic">
                    {optoutCount}
                  </Text>
                </Pill>
              )}
              {rejectCount > 0 && (
                <Pill
                  renderIcon={CheckinStore.statusOptions.REJECT.icon}
                  color="danger"
                  margin="none none none x-small"
                >
                  <Text size="x-small" fontStyle="italic">
                    {rejectCount}
                  </Text>
                </Pill>
              )}
            </>
          </Flex.Item>
          <Flex.Item>
            {completeCount < totalCount ? (
              <Pill margin="none none none x-small">
                <Text size="x-small" fontStyle="italic">
                  {completeCount} of {totalCount}
                </Text>
              </Pill>
            ) : (
              <IconButton
                as="div"
                color="success"
                screenReaderLabel="Mark all complete"
                shape="circle"
                size="small"
                cursor="arrow"
              >
                <IconCheckMarkSolid />
              </IconButton>
            )}
          </Flex.Item>
        </Flex>
      }
    >
      <Table caption="Top rated movies" hover>
        <Table.Head>
          <Table.Row>
            <Table.ColHeader width="10%" id="Rank">
              Status
            </Table.ColHeader>
            <Table.ColHeader width="30%" id="Title">
              Title
            </Table.ColHeader>
            <Table.ColHeader width="50%" id="Year">
              Comment
            </Table.ColHeader>
            <Table.ColHeader width="10%" id="Rating"></Table.ColHeader>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {Object.entries(CheckinStore.getGroupItems(group.id)).map(
            ([itemId, item]) => (
              <Table.Row key={`item-${itemId}`}>
                <Table.RowHeader>
                  <ItemIcon item={item} />
                </Table.RowHeader>
                <Table.Cell>{item.title}</Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell textAlign="center">{renderButton(item)}</Table.Cell>
              </Table.Row>
            )
          )}
        </Table.Body>
      </Table>
    </ToggleGroup>
  );
};

export default CheckinGroup;
